/**
 * The hero film played through YouTube's embedded player.
 *
 * <p>Everything here is a way round something the player does on its own. It reports its state over
 * `postMessage` rather than through the DOM, it draws its own startup indicator over the first
 * frames, it keeps a title bar that `modestbranding` stopped hiding in 2024, and it refuses autoplay
 * often enough that a film that never starts has to be an expected outcome. The self-hosted delivery
 * in `hero-film-video.ts` needs none of it.</p>
 *
 * <p>The frame is assembled with `document.createElement` and never from a markup string: the
 * literal tag anywhere in the bundle fails `tools/check-prototype.py`, which reads the shipped code
 * into the page it checks.</p>
 */
import type { HeroFilmPlayer, HeroFilmSignals } from "./hero-film-player";

const PLAYER_ORIGIN = "https://www.youtube-nocookie.com";

/** YouTube's state number for a player that is actually playing. */
const PLAYING = 1;

/**
 * How far into the film to wait before saying there is a picture.
 *
 * <p>The player draws a play/pause indicator over its own first frames, and `controls=0` does not
 * remove it — it fades on its own once the film is running. Announcing on the playing state alone
 * put that indicator on the hero. A film that has genuinely advanced this far is past it.</p>
 */
const PAST_THE_OVERLAY = 0.4;

/** Announced regardless after this long playing, in case the player stops reporting its position. */
const OVERLAY_FALLBACK = 900;

/** Parses a message from the player, or returns null when it is not one. */
function playerMessage(data: unknown): { event?: unknown; info?: unknown } | null {
  if (typeof data !== "string") {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(data);
    return typeof parsed === "object" && parsed !== null ? parsed as { event?: unknown; info?: unknown } : null;
  } catch {
    return null;
  }
}

/** True when the message says the player has reached the playing state. */
function saysItIsPlaying(message: { info?: unknown }): boolean {
  // Two shapes carry the state: onStateChange puts it in `info`, and the infoDelivery messages the
  // player sends while running put it in `info.playerState`.
  if (message.info === PLAYING) {
    return true;
  }

  const info = message.info as { playerState?: unknown } | null | undefined;
  return info?.playerState === PLAYING;
}

/** How far into the film the message says the player has reached, or null when it does not say. */
function positionIn(message: { info?: unknown }): number | null {
  const info = message.info as { currentTime?: unknown } | null | undefined;
  return typeof info?.currentTime === "number" ? info.currentTime : null;
}

/**
 * Builds a player for a film on YouTube.
 *
 * <p>`modestbranding` is not among the parameters because YouTube stopped honouring it in 2024. What
 * keeps the picture bare is geometry: a 16:9 film covering a 21:9 box overflows by about a third of
 * its height, so the player's own title sits above the visible edge. `pointer-events: none` on the
 * layer means none of it can be hovered into view either. `loop` needs `playlist` naming the same id
 * — that is YouTube's way of looping a single film — and without it the end screen would drop a grid
 * of related videos onto the hero.</p>
 *
 * <p>`enablejsapi` is here for one reason: it is what lets the player tell us it has started, so the
 * fade can wait for a picture instead of a black rectangle.</p>
 */
export function embeddedHeroFilm(videoId: string): HeroFilmPlayer {
  const frame = document.createElement("iframe");
  frame.title = "Film från Åmund Vin och Fruktgård";
  frame.allow = "autoplay; encrypted-media; picture-in-picture";
  frame.tabIndex = -1;
  frame.setAttribute("aria-hidden", "true");

  let signals: HeroFilmSignals | null = null;
  let running = false;
  let overlayFallback = 0;

  /**
   * Tells the gate that a picture is on screen and that the film is moving, once and no more.
   *
   * <p>Both facts at the same moment, which is particular to this delivery: the frame is black
   * until the player is past its own startup indicator, so there is nothing worth revealing before
   * the film is already running. A video file reaches the two separately.</p>
   */
  function reveal(): void {
    window.clearTimeout(overlayFallback);
    signals?.show();
    signals?.running();
    signals = null;
  }

  /** Reads the player's own messages and decides from them whether a picture has appeared. */
  function listen(event: MessageEvent): void {
    if (event.origin !== PLAYER_ORIGIN || event.source !== frame.contentWindow) {
      return;
    }

    const message = playerMessage(event.data);
    if (message === null) {
      return;
    }

    // Asking rather than trusting autoplay. It costs one message and covers the browsers that would
    // otherwise leave the player sitting at playerState -1, unstarted.
    if (message.event === "onReady") {
      frame.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }), PLAYER_ORIGIN);
      return;
    }

    if (!running && saysItIsPlaying(message)) {
      running = true;
      overlayFallback = window.setTimeout(reveal, OVERLAY_FALLBACK);
    }

    // The position keeps arriving while the film runs, so this is the first report from past the
    // player's own startup indicator rather than a second timer guessing at it.
    if (running && (positionIn(message) ?? 0) >= PAST_THE_OVERLAY) {
      reveal();
    }
  }

  /** Asks the player to report its state; it only talks to a parent that has asked to hear it. */
  function askToBeTold(): void {
    frame.contentWindow?.postMessage(
      JSON.stringify({ event: "listening", id: videoId, channel: "widget" }), PLAYER_ORIGIN);
  }

  return {
    element: frame,

    start(onSignal: HeroFilmSignals): void {
      signals = onSignal;

      const parameters = [
        "autoplay=1",
        // Autoplay is refused with sound in every browser. Not a preference.
        "mute=1",
        "loop=1",
        `playlist=${encodeURIComponent(videoId)}`,
        "controls=0",
        "disablekb=1",
        "iv_load_policy=3",
        "rel=0",
        "playsinline=1",
        "enablejsapi=1",
        `origin=${encodeURIComponent(window.location.origin)}`,
      ].join("&");

      window.addEventListener("message", listen);
      frame.addEventListener("load", askToBeTold, { once: true });
      frame.src = `${PLAYER_ORIGIN}/embed/${encodeURIComponent(videoId)}?${parameters}`;
    },

    stop(): void {
      signals = null;
      window.clearTimeout(overlayFallback);
      window.removeEventListener("message", listen);
      frame.removeEventListener("load", askToBeTold);
      frame.remove();
    },
  };
}
